import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faChartLine, faRocket } from '@fortawesome/free-solid-svg-icons';

const TradingLevels: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const levels = [
    {
      id: 0,
      title: "Beginner",
      icon: faGraduationCap,
      color: "#784dcc",
      description: "Start your trading journey with a solid foundation. Learn basic concepts such as market types, trading platforms, and essential trading tools.",
      features: [
        "Understanding market trends and patterns",
        "Getting familiar with trading terminology",
        "Exploring demo trading accounts"
      ]
    },
    {
      id: 1,
      title: "Intermediate",
      icon: faChartLine,
      color: "#784dcc",
      description: "Enhance your skills by mastering risk management, technical analysis, and developing personalized trading strategies.",
      features: [
        "Reading price action, identifying market structure, and spotting high-probability setups",
        "Managing risk with stop-loss and take-profit orders",
        "Creating a disciplined trading routine"
      ]
    },
    {
      id: 2,
      title: "Advanced",
      icon: faRocket,
      color: "#784dcc",
      description: "Dive into advanced techniques, algorithmic trading, and explore global markets to maximize your potential.",
      features: [
        "Understanding derivatives and futures trading",
        "Leveraging automated trading systems",
        "Executing advanced chart pattern analysis"
      ]
    }
  ];

  return (
    <Container>
      <MainWrapper>
        <Header>Know about our <span style={{ color: '#784dcc' }}>Trading Levels</span></Header>
        <Description>
          Take our in-depth market knowledge test for free! We assess your trading proficiency to help you determine which course level is the best fit for you.
          <br /><br />
          By utilizing insights from your test results, you can choose the most suitable program to enhance your trading skills.
        </Description>

        <TabsContainer>
          <TabsList>
            {levels.map((level) => (
              <Tab
                key={level.id}
                onClick={() => setActiveTab(level.id)}
                active={activeTab === level.id}
                themeColor={level.color}
              >
                <TabIcon themeColor={level.color} active={activeTab === level.id}>
                  <FontAwesomeIcon icon={level.icon} />
                </TabIcon>
                <span>{level.title}</span>
              </Tab>
            ))}
          </TabsList>

          <TabContent>
            <ContentCard>
              <ContentHeader>
                <IconContainer themeColor={levels[activeTab].color}>
                  <FontAwesomeIcon icon={levels[activeTab].icon} className="icon" />
                </IconContainer>
                <ContentTitle themeColor={levels[activeTab].color}>
                  {levels[activeTab].title}
                </ContentTitle>
              </ContentHeader>

              <ContentDescription>
                {levels[activeTab].description}
              </ContentDescription>

              <FeaturesList>
                {levels[activeTab].features.map((feature, idx) => (
                  <FeatureItem key={idx}>
                    <CheckIcon themeColor={levels[activeTab].color}>✓</CheckIcon>
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeaturesList>
            </ContentCard>
          </TabContent>
        </TabsContainer>
      </MainWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 60px 20px;
  background: #E7ECF9;
`;

const MainWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.h1`
  text-align: center;
  color: #000;
  font-weight: 500;
  font-size: 45px;
  font-family: var(--font-bold);
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Description = styled.p`
  text-align: center;
  font-size: 17px;
  color: #666666;
  margin: 0 auto 50px;
  max-width: 800px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 30px;
  }
`;

const TabsContainer = styled.div`
  margin-bottom: 60px;
`;

const TabsList = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Tab = styled.button<{ active: boolean; themeColor: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 30px;
  background: ${({ active }) => active ? '#fff' : 'transparent'};
  border: 2px solid ${({ active, themeColor }) => active ? themeColor : '#ddd'};
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;
  font-weight: 600;
  font-family: var(--font-semiBold);
  color: ${({ active }) => active ? '#333' : '#666'};
  box-shadow: ${({ active }) => active ? '0 5px 15px rgba(0, 0, 0, 0.1)' : 'none'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    border-color: ${({ themeColor }) => themeColor};
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 12px 20px;
  }
`;

const TabIcon = styled.div<{ themeColor: string; active: boolean }>`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(145deg, ${props => props.themeColor}20, ${props => props.themeColor}40);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    font-size: 18px;
    color: ${props => props.themeColor};
  }
`;

const TabContent = styled.div`
  animation: fadeIn 0.4s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContentCard = styled.div`
  background: #fff;
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
`;

const IconContainer = styled.div<{ themeColor: string }>`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(145deg, ${props => props.themeColor}15, ${props => props.themeColor}30);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  .icon {
    font-size: 32px;
    color: ${props => props.themeColor};
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    
    .icon {
      font-size: 28px;
    }
  }
`;

const ContentTitle = styled.h3<{ themeColor: string }>`
  font-size: 32px;
  font-weight: 600;
  font-family: var(--font-bold);
  color: ${props => props.themeColor};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ContentDescription = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 15px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: #555;
  
  span {
    flex: 1;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CheckIcon = styled.span<{ themeColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${props => props.themeColor};
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 2px;
`;

export default TradingLevels;
